import { useState } from 'react';
import type { Package, PackageKind } from '@salonomy/types';
import { Button, Card, EmptyState, Pill, Spinner, Toggle } from '@salonomy/ui';
import { ConfirmModal } from '../../components/ConfirmModal.js';
import { usePackages, useUpdatePackage } from '../../data/packages.js';
import { useServices } from '../../data/services.js';
import { money } from '../../lib/format.js';
import { useToast } from '../../lib/toast.js';
import { PackageEditorModal } from './PackageEditorModal.js';
import './packages.css';

const KIND_LABEL: Record<PackageKind, string> = {
  prepaid_sessions: 'Prepaid sessions',
  prepaid_value: 'Prepaid value',
  membership_recurring: 'Membership',
};

/** Sum of a package's granted sessions across its items (display-only — not a money figure). */
function totalSessions(pkg: Package): number | null {
  const sessions = pkg.items.map((it) => it.sessionsGranted).filter((n): n is number => n != null);
  if (sessions.length === 0) return null;
  return sessions.reduce((s, n) => s + n, 0);
}

/**
 * The catalogue packages list — a paged table off `usePackages`, mirroring ProductsPanel. Each row shows
 * the catalogue price, kind, granted sessions and validity; price is the catalogue list price (a sold
 * instrument freezes its own — never recomputed here). A row can be edited (manager) or archived (soft —
 * active:false). The active filter defaults to active-only but can include archived so a deactivated
 * package stays inspectable.
 */
export function PackagesPanel() {
  const toast = useToast();
  const [includeInactive, setIncludeInactive] = useState(false);
  const [offset, setOffset] = useState(0);
  const [modal, setModal] = useState<{ existing?: Package } | null>(null);
  const [archiveTarget, setArchiveTarget] = useState<Package | null>(null);
  const [busy, setBusy] = useState(false);

  const { data: services } = useServices();
  const update = useUpdatePackage();

  // active:true = active-only; undefined returns active+inactive.
  const { data, isLoading, isError, isFetching } = usePackages(
    includeInactive ? undefined : true,
    offset,
  );

  const rows = data?.data ?? [];
  const total = data?.pagination.total ?? 0;
  const limit = data?.pagination.limit ?? 50;

  async function confirmArchive() {
    if (!archiveTarget) return;
    setBusy(true);
    try {
      // Archive = a soft deactivate via the attribute PATCH (no DELETE on the API); the package stays
      // inspectable under "Show archived".
      await update.mutateAsync({ id: archiveTarget.id, body: { active: false } });
      toast.success(`${archiveTarget.name} archived`);
      setArchiveTarget(null);
    } catch {
      toast.error('Could not archive this package.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card>
      <div className="pkg-head">
        <div>
          <div className="section-title" style={{ marginBottom: 2 }}>
            Packages
          </div>
          <div className="muted" style={{ fontSize: 13 }}>
            Prepaid courses and stored-value packages. A sale is held as a liability — revenue is
            recognised only when sessions are redeemed.
          </div>
        </div>
        <Button size="sm" onClick={() => setModal({})}>
          New package
        </Button>
      </div>

      <div className="pkg-toggles">
        <Toggle
          checked={includeInactive}
          onChange={(v) => {
            setIncludeInactive(v);
            setOffset(0);
          }}
          label="Show archived"
        />
      </div>

      {isLoading ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '16px 0' }}>
          <Spinner size={16} />
          <span className="muted" style={{ fontSize: 13 }}>
            Loading the catalogue…
          </span>
        </div>
      ) : isError ? (
        <div className="muted" style={{ fontSize: 13, padding: '8px 0' }}>
          Couldn&rsquo;t load packages. Your package catalogue may not be set up yet — add one to
          begin.
        </div>
      ) : rows.length === 0 ? (
        <EmptyState
          title="No packages yet"
          description="Create a prepaid course or a stored-value package to sell at the till."
          action={
            <Button size="sm" onClick={() => setModal({})}>
              New package
            </Button>
          }
        />
      ) : (
        <div
          className="pkg-table"
          style={{ ['--cols' as string]: 'minmax(0,2fr) 1fr 0.9fr 0.9fr 0.9fr 1.2fr' }}
        >
          <div className="pkg-row pkg-row--head">
            <span>Package</span>
            <span>Kind</span>
            <span className="pkg-num">Price</span>
            <span className="pkg-num">Sessions</span>
            <span className="pkg-num">Validity</span>
            <span className="pkg-actions">Actions</span>
          </div>
          {rows.map((p) => {
            const sessions = totalSessions(p);
            return (
              <div key={p.id} className="pkg-row">
                <div className="pkg-cell-main">
                  <span className="pkg-cell-main__name">{p.name}</span>
                  <span className="muted" style={{ fontSize: 12 }}>
                    {p.purpose === 'single' ? 'Single-purpose' : 'Multi-purpose'}
                    {!p.active ? ' · archived' : ''}
                  </span>
                </div>
                <span>{KIND_LABEL[p.kind]}</span>
                <span className="pkg-num mono">{money(p.pricePence)}</span>
                <span className="pkg-num mono">{sessions != null ? sessions : '—'}</span>
                <span className="pkg-num mono">
                  {p.validityDays != null ? `${p.validityDays}d` : '∞'}
                </span>
                <span className="pkg-actions">
                  <Button size="sm" variant="ghost" onClick={() => setModal({ existing: p })}>
                    Edit
                  </Button>
                  {p.active ? (
                    <Button size="sm" variant="ghost" onClick={() => setArchiveTarget(p)}>
                      Archive
                    </Button>
                  ) : (
                    <Pill tone="soft">Archived</Pill>
                  )}
                </span>
              </div>
            );
          })}
        </div>
      )}

      {total > limit ? (
        <div className="pkg-pager">
          <Button
            variant="outline"
            size="sm"
            disabled={offset === 0 || isFetching}
            onClick={() => setOffset((o) => Math.max(0, o - limit))}
          >
            ← Previous
          </Button>
          <span className="muted" style={{ fontSize: 12.5 }}>
            {offset + 1}–{Math.min(offset + limit, total)} of {total}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={offset + limit >= total || isFetching}
            onClick={() => setOffset((o) => o + limit)}
          >
            Next →
          </Button>
        </div>
      ) : null}

      {modal ? (
        <PackageEditorModal
          existing={modal.existing}
          services={services ?? []}
          onClose={() => setModal(null)}
        />
      ) : null}

      <ConfirmModal
        open={Boolean(archiveTarget)}
        title="Archive package"
        confirmLabel="Archive"
        confirmPhrase={archiveTarget?.name}
        busy={busy}
        onConfirm={confirmArchive}
        onClose={() => setArchiveTarget(null)}
      >
        <p style={{ margin: 0 }}>
          Archiving <strong>{archiveTarget?.name}</strong> hides it from the catalogue and the till.
          Already-sold instruments keep working — clients can still redeem the sessions they bought.
          You can reactivate it later by editing it (Show archived).
        </p>
      </ConfirmModal>
    </Card>
  );
}
